import React from 'react'
import { useTranslation } from 'react-i18next'
import { LineData } from './LineData'
import { RoomMasterType } from '../availability-tab/type'

interface Props {
  roomMaster: RoomMasterType
  close: () => void
}

export const RoomInfo = ({ roomMaster, close }: Props): JSX.Element => {
  const [isOpenFloorImage, setIsOpenFloorImage] = React.useState(false)
  const { t } = useTranslation()

  return (
    <>
      <div className="modal-backdrop fixed z-50 top-0 left-0 right-0 w-full overflow-x-hidden overflow-y-auto max-h-full h-full flex md:inset-0">
        <div className="flex flex-col items-start gap-4 p-3 bg-white overflow-y-auto fixed top-0 right-0 bottom-0 right-drawer">
          <div className="overflow-y-auto">
            <div className="flex mb-3">
              <div className="font-bold font-size-16 text-black-900 mt-2">
                {t('landing_page.avaiability.room_master.drawer_title')}
              </div>
              <button
                className="text-gray-900 bg-transparent hover:bg-gray-200 rounded-lg text-sm ml-auto inline-flex items-center dark:hover:bg-gray-600 dark:hover:text-white p-1.5"
                type="button"
                onClick={close}
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path
                    clipRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    fillRule="evenodd"
                  />
                </svg>
              </button>
            </div>

            <div className="flex gap-2 overflow-auto hidden-scrollbar w-full mb-3">
              {roomMaster.images.map((image, index) => (
                <img
                  key={`room_image_${index}`}
                  src={image}
                  alt={`Room Image ${index + 1}`}
                  className="rounded-lg object-cover h-50"
                />
              ))}
            </div>

            <div className="flex flex-col items-start gap-4 w-full">
              <div className="flex flex-col items-start gap-2 w-full">
                <div className="font-bold font-size-16 text-black-900">{roomMaster.name}</div>
                <table className="w-full">
                  <tbody>
                    <LineData label={t('plan_detail.room_info.accommodation_type')} value={roomMaster.roomType} />
                    <LineData
                      label={t('plan_detail.room_info.capacity.title')}
                      value={`${roomMaster.roomMaxCount}${t('reservation_confirm.modal_booking.people')}`}
                    />
                    <LineData label={t('plan_detail.room_info.kitchen')} value={roomMaster.kitchen} />
                    <LineData label={t('plan_detail.room_info.bedroom')} value={roomMaster.bedroom?.join('、')} />
                    <LineData
                      label={t('landing_page.avaiability.room_master.bathroom')}
                      value={roomMaster.bathroom?.join('、')}
                    />
                    <LineData label={t('plan_detail.room_info.laundry')} value={roomMaster.laundry} />
                    <LineData label={t('landing_page.avaiability.room_master.food')} value={roomMaster.foodText} />
                    <LineData
                      label={t('landing_page.avaiability.room_master.environment')}
                      value={roomMaster.roomEnvironment?.join('、')}
                    />
                    <LineData
                      label={t('landing_page.avaiability.room_master.work_environment')}
                      value={roomMaster.conditionsForRemoteWork}
                    />
                    <LineData
                      label={t('landing_page.avaiability.room_master.access_way')}
                      value={roomMaster.accessFromGuesthouseToNursery}
                    />
                  </tbody>
                </table>
              </div>

              {roomMaster.floorImages.length > 0 && (
                <div className="w-full mb-6">
                  <button
                    className="flex items-center justify-center py-3 rounded border border-solid border-green w-full"
                    onClick={() => setIsOpenFloorImage(true)}
                  >
                    <div className="flex items-center justify-center w-full gap-2">
                      <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_15_113)">
                          <path
                            d="M9.5 11H7.5V13H9.5V11ZM13.5 11H11.5V13H13.5V11ZM17.5 11H15.5V13H17.5V11ZM19.5 4H18.5V2H16.5V4H8.5V2H6.5V4H5.5C4.39 4 3.51 4.9 3.51 6L3.5 20C3.5 21.1 4.39 22 5.5 22H19.5C20.6 22 21.5 21.1 21.5 20V6C21.5 4.9 20.6 4 19.5 4ZM19.5 20H5.5V9H19.5V20Z"
                            fill="#189855"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_15_113">
                            <rect width="24" height="24" fill="white" transform="translate(0.5)" />
                          </clipPath>
                        </defs>
                      </svg>
                      <span className="w-fit font-regular-regular14 whitespace-nowrap text-green">
                        {t('landing_page.avaiability.room_master.check_floor_plan')}
                      </span>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {isOpenFloorImage && (
        <div className="modal-backdrop fixed z-50 top-0 left-0 right-0 w-full h-full max-h-full overflow-hidden">
          <div className="flex flex-col items-center h-full">
            <div className="relative m-auto text-center">
              <button
                className="absolute right-2"
                type="button"
                onClick={() => setIsOpenFloorImage(false)}
                style={{ top: '10px' }}
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path
                    clipRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    fillRule="evenodd"
                  />
                </svg>
              </button>
              <img src={roomMaster.floorImages[0]} alt="Floor Plan" style={{ maxHeight: '400px' }} />
            </div>
          </div>
        </div>
      )}
    </>
  )
}
